import {
  Calendar,
  DateRangeType,
  DayOfWeek,
  getDateRangeArray,
  Label,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  Pivot,
  PivotItem,
  Stack,
  Spinner,
  SpinnerSize,
  DefaultButton,
  Icon,
  Separator,
} from '@fluentui/react';
import { useEffect, useRef, useState } from 'react';
import { IMAGES } from '../../../config/images';
import * as DateUtil from '../../../util/Date';
import { PreferredDoctorPersona } from '../../core/PreferredDoctorPersona';
import { TimeSlots } from '../components/TimeSlots';
import { HealAPI } from '../../../healApi';
import { TimeSlotData } from '../../../api/timeslots.types';
import { useCart } from '../CartContext';
import { usePatientVisits } from '../PatientVisitsContext';
import moment from 'moment';
import { getZeroTimeDate } from '../../../util/Date';
import { Timeslot } from '../../../api/markets.types';
import _ from 'lodash';
import { PatientVisits } from '../../patient/PatientVisits';

interface DateTimeslotOptions {
  proximity: Timeslot[];
  capacity: Timeslot[];
}

export function SelectTime() {
  const { state: cart, dispatch } = useCart();
  // If the patient has a PCP set, default to showing slots for that doctor instead of Other.
  const [bookWithPcp, setBookWithPcp] = useState(!!cart.who.patient?.preferredDoctor);
  const [fetching, setFetching] = useState(false);
  const [timeslots, setTimeslots] = useState<TimeSlotData[]>();
  const [recommendedMode, setRecommendedMode] = useState(true);
  const range = getDateRangeArray(cart.when.date || new Date(), DateRangeType.Week, DayOfWeek.Sunday).filter((d) => d.getTime() >= getZeroTimeDate().getTime());

  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>(range);
  const [selectedDate, setSelectedDate] = useState<Date>(cart.when.date || new Date());
  const [data, setData] = useState<Record<string, DateTimeslotOptions>>({});
  const proximals = useRef<Set<string>>();
  const [dataErrorMessage, setDataErrorMessage] = useState<string>();

  const [patientVisits] = usePatientVisits();

  useEffect(() => {
    if (recommendedMode) {
      return;
    }
    setFetching(true);
    HealAPI.fetchTimeSlots(cart.what.serviceCode, cart.who.patientId, cart.where.addressId, undefined, bookWithPcp)
      .then((r) => (r.ok ? r.data : undefined))
      .then((data) => {
        if (data) {
          // Add the proximal boolean to slot results that match preferred slots identified in `timeslots`.
          // Skip this step if Recommended results haven't come in yet (if proximals is undefined).
          if (proximals) {
            for (let i = 0; i < data.length; i++) {
              data[i].proximal = proximals.current?.has(data[i].id);
            }
          }
          setTimeslots(data);
        }
        setFetching(false);
      });
  }, [cart.who.patientId, cart.what.serviceCode, cart.where.addressId, recommendedMode, bookWithPcp]);

  const renderPreferredDoctor = () => (
    <Stack horizontal>
      <Stack.Item grow={1}>
        <div style={{ padding: 20, background: bookWithPcp ? '#d4ecff' : '#fff', cursor: 'pointer' }} onClick={() => setBookWithPcp(true)}>
          <PreferredDoctorPersona preferredDoctor={{ isFetching: false, data: cart.who.patient?.preferredDoctor }} />
        </div>
      </Stack.Item>
      <Stack.Item grow={1}>
        <div style={{ padding: 20, background: bookWithPcp ? '#fff' : '#d4ecff', cursor: 'pointer' }} onClick={() => setBookWithPcp(false)}>
          <Persona size={PersonaSize.size72} text="Other" secondaryText="Any available provider" imageUrl={IMAGES.doctor} />
        </div>
      </Stack.Item>
    </Stack>
  );

  useEffect(() => {
    if (!recommendedMode) {
      return;
    }
    setFetching(true);
    const start = moment(selectedDateRange[0]).format('YYYY-MM-DD');
    const end = moment(selectedDateRange[selectedDateRange.length - 1]).format('YYYY-MM-DD');
    const selectedDoctorId = bookWithPcp ? cart.who.patient?.preferredDoctor?.doctor?.providerId : undefined;
    setData({});
    setDataErrorMessage(undefined);
    HealAPI.fetchRecommendedTimeslotsForProvider(start, end, cart.where.latitude, cart.where.longitude, selectedDoctorId, cart.what.serviceCode).then((response) => {
      if (response.ok) {
        setData(response.data);

        /**
         * Identify and separately store a list of all of the slot IDs that will be given the "preferred" star.
         * This will then be used to star the same items in the "All Timeslots" tab, which doesn't otherwise receive this data.
         */
        const proximalIds = new Set<string>();
        const allSlots = (Object.values(response.data) as DateTimeslotOptions[]).flatMap((slot) => [...slot.capacity, ...slot.proximity]);
        for (const slot of allSlots) {
          if (slot.proximal) {
            proximalIds.add(slot.id);
          }
        }
        proximals.current = proximalIds;
      } else {
        setDataErrorMessage(JSON.stringify(response.data));
        proximals.current = undefined;
      }
      setFetching(false);
    });
  }, [cart.who, cart.where, cart.what.serviceCode, recommendedMode, bookWithPcp, selectedDateRange]);

  const onTimeslotChange = (timeslot: any) => {
    dispatch({ type: 'set_when', data: { pcp: bookWithPcp, ...timeslot } });
  };

  const onSelectDate = (date: Date, dateRangeArray?: Date[]): void => {
    setSelectedDate(date);
    setSelectedDateRange(dateRangeArray!);
  };

  const renderTimeslots = (date: string, slots: Timeslot[]) => {
    const sorted = _.sortBy(slots, 'startTime');
    return (
      <div key={date}>
        <Label>{moment(date).format('MM/DD/YYYY')}</Label>
        {!sorted.length && <MessageBar messageBarType={MessageBarType.info}>There are no slots on this day.</MessageBar>}
        <div>
          {sorted.map((t, i) => {
            const selected = cart.when.timeSlotId === t.id;
            const preferred = data[date].proximity.some((tt: Timeslot) => tt.id === t.id);
            return (
              <DefaultButton
                toggle
                style={{ width: 210, marginRight: 6, marginBottom: 6, color: preferred ? '#0078d4' : 'unset', backgroundColor: selected ? 'rgb(212, 236, 255)' : 'unset' }}
                checked={cart.when.timeSlotId === t.id}
                key={`${date}_${t.id}`}
                text={`${t.longTimeString}`}
                iconProps={preferred ? { iconName: 'FavoriteStarFill', style: { color: '#0078d4' } } : { iconName: 'undefined' }}
                onClick={() =>
                  onTimeslotChange({
                    date: new Date(t.longDateString),
                    timeSlotId: t.id,
                    timeSlot: t.longTimeString,
                  })
                }
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Stack>
      <Label className="ms-font-xl">Select Time</Label>
      {!!cart.who.patient?.preferredDoctor?.doctor?.providerId && renderPreferredDoctor()}
      <Pivot onLinkClick={(i) => setRecommendedMode(i?.props.itemKey === 'recommended')} selectedKey={recommendedMode ? 'recommended' : 'alltimeslots'}>
        <PivotItem headerText="Recommended" itemKey="recommended">
          <Calendar
            onSelectDate={onSelectDate}
            strings={DateUtil.DatePickerStrings}
            minDate={new Date()}
            isMonthPickerVisible={true}
            highlightSelectedMonth={true}
            showGoToToday={false}
            dateRangeType={DateRangeType.Week}
            value={selectedDate!}
            firstDayOfWeek={DayOfWeek.Sunday}
            isDayPickerVisible={true}
          />

          {dataErrorMessage && <MessageBar messageBarType={MessageBarType.error}>{dataErrorMessage}</MessageBar>}
          <MessageBar messageBarType={MessageBarType.info}>
            The time slots with a <Icon iconName="FavoriteStarFill" style={{ color: '#0078d4' }} /> are recommended based on existing visit locations.
          </MessageBar>
          {fetching && <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Getting recommended timeslots..." />}
          {
            <Stack root={{ style: { marginTop: 10 } }} tokens={{ childrenGap: 12 }}>
              {Object.keys(data)
                .sort()
                .map((d) => renderTimeslots(d, data[d].capacity.concat(data[d].proximity)))}
            </Stack>
          }
        </PivotItem>
        <PivotItem headerText="All Timeslots" itemKey="alltimeslots">
          <TimeSlots timeslots={{ isFetching: fetching, data: timeslots }} when={cart.when?.timeSlotId ? (cart.when as any) : undefined} onChanged={onTimeslotChange} />
        </PivotItem>
      </Pivot>

      <Separator />
      {patientVisits && <PatientVisits visits={patientVisits} title={'Upcoming Visits'} />}
    </Stack>
  );
}
